/**
 * PriorsBundle — the mapping subsystem's knowledge as versioned DATA
 * (ADR-004 §4, §6): synonym lexicon (compiled from v2's platform tables),
 * with room for anthropometric ratios and user corrections later. Priors are
 * passed in, never baked-in constants — the app owns loading/persisting.
 * An empty or unparseable bundle is a boot error, not a "run without
 * priors" fallback (ADR-004 §7).
 * Depends on: zod, ir/errors, ir/ontology (validation). Depended on by:
 * kernel/mapping, app.
 */
import { z } from 'zod';
import { SchemaValidationError } from './errors.js';
import { vec3Schema, type Ontology, type Vec3 } from './ontology.js';

export const synonymSchema = z.object({
  /** The bone name as a platform authored it, verbatim. */
  name: z.string().min(1),
  /**
   * Provenance: which knowledge source contributed it — e.g. 'v2:mixamo',
   * 'v2:ready_player_me' (platform deprecated 2026-07-11, knowledge kept),
   * 'v2:fuzzy', 'corpus:<specimen>', 'user:<id>'.
   */
  source: z.string().min(1),
});
export type Synonym = z.infer<typeof synonymSchema>;

export const lexiconEntrySchema = z.object({
  /** BBSA ontology ID the synonyms map to. */
  ontologyId: z.string().min(1),
  synonyms: z.array(synonymSchema).min(1),
});
export type LexiconEntry = z.infer<typeof lexiconEntrySchema>;

/**
 * A per-family, per-ontology-id AUTHORED joint-frame override — the Hand
 * Studio frame contract (reframed 2026-07-14 with Allen; ADR-010 §11's
 * hand-correctable pattern applied to frames). NOT a measurement: an explicit
 * human/tool declaration — "for family X, bone Y, the local frame is P/S,
 * because the author said so."
 *
 * Why it exists: the residual declared-frame bones (clavicles, hands) are
 * exactly where geometry can't measure a frame — and Hand Studio's GRAFTED
 * finger bones are the extreme case: bones that never had an authored bind at
 * all. The graft step authors their frames at creation time (`localPrimary` =
 * pointing along the digit, `localSecondary` = the curl-axis reference — get
 * the curl axis wrong and a fist bends sideways); the correction UI writes
 * fixed entries when the auto-graft guesses wrong. Family-keyed so authoring
 * the Meshy hand frames ONCE serves every Meshy import, and one pose library
 * (fist/relaxed/point) rotates correctly into every family's fingers.
 *
 * Epistemics: authored = full authority for that bone (the hand wins over
 * automation — ADR-010 §11); geometry violations are rejected at validation
 * (unit/⊥ below), so a typo cannot ship, but there are no confidence tiers and
 * no promotion gates — nothing statistical to round-trip. (The earlier
 * measured-family-prior semantics were superseded 2026-07-13: the bilateral
 * -child tier measures center bones from bind geometry, and the family
 * measured-primary path was proven to have no runtime customer.)
 *
 * Producer: Hand Studio's graft step + its correction UI (`authored:hand-
 * studio`, `authored:user:<id>`), or hand-authored data (`authored:ontology`).
 * Consumer: the reorientation ladder's authored tier (PR D 2/n wires it) +
 * pose application. An ABSENT entry is never a failure — reorientation falls
 * through to its measured/declared tiers.
 */
export const jointFrameSchema = z.object({
  /**
   * Platform-generator family the frame is authored for — an open slug
   * (`mixamo`, `tripo`, `meshy`, `bbsa-default`), NOT a closed enum: the suite
   * invests in auto-targeting, not a frozen platform table. Matched against
   * the family the orchestrator declares for a rig.
   */
  family: z.string().regex(/^[a-z0-9-]+$/),
  /** BBSA ontology ID this frame is for. */
  ontologyId: z.string().min(1),
  /** Optional family pairing constraint for compatible secondary references. */
  pairedFamilies: z.array(z.string().regex(/^[a-z0-9-]+$/)).min(1).optional(),
  /** Restrict a correction to one side of retargeting; absent means either. */
  appliesTo: z.enum(['source', 'target']).optional(),
  /** Bone-local unit direction the bone points (joint → primaryChild). */
  localPrimary: vec3Schema,
  /** Bone-local unit direction ⊥ localPrimary — the authored twist/curl reference. */
  localSecondary: vec3Schema,
  /**
   * What the secondary NAMES on the body, when it names something the rig's
   * own convention can check: `anterior` = the character's forward at bind.
   * Declared so the agreement guard (retarget/authored-frame-guard) can
   * refuse a frame whose rig contradicts it — a family keyed by names can
   * meet a skeleton with another generator's roll (Tripo presets,
   * 2026-09-23). Absent = no body-level meaning (the graft's dorsal finger
   * frames): the guard checks the primary only.
   */
  secondaryMeans: z.enum(['anterior']).optional(),
  /**
   * The authoring trail, reviewable — e.g. `['authored:hand-studio']`,
   * `['authored:user:allen', 'replaces:authored:hand-studio']`. At least one
   * entry MUST carry the `authored:` prefix (enforced in parsePriorsBundle):
   * this table holds declarations, not measurements — the machine-checked
   * form of the reframe.
   */
  provenance: z.array(z.string().min(1)).min(1),
});
export type JointFrame = z.infer<typeof jointFrameSchema>;

/**
 * ADR-028: a family's hinge-corrective RULE — never its axes. The template
 * declares which bones hinge, the world anterior each bends toward, the
 * helper's share of the bend, and the crease band; the CONCRETE axes are
 * measured per character at graft time (graft-hinge-helpers), because the
 * family-constant assumption was falsified by measurement: Tripo rolls bones
 * arbitrarily about their long axis WITHIN the family (amari R_Forearm hinge
 * local (0.98,0,−0.21), goblin (−1,0,0)).
 */
/**
 * ADR-028 E2: one muscle leaf riding a hinge — the bicep belly, the forearm
 * flexor mass. The direction is declared ANATOMICALLY ({anterior, proximal,
 * lateral} components), never as global axes: an A-pose limb's "proximal" is
 * along the limb toward its root, which no world constant names — the graft
 * resolves the frame from the measured limb axis and the rig's promoted
 * facing, the same posture as the hinge axes themselves. The ramp's values
 * are fractions of the SEGMENT's length (§6: a family-shared number cannot
 * carry millimetres); the door multiplies by the character's measured length.
 */
export const hingeLeafSchema = z.object({
  /** Leaf bone name — wears the `_corr` class like every corrective. */
  name: z.string().regex(/_corr$/),
  /** Which segment the leaf rides: proximal (bicep on the humerus) or distal (forearm flexor). */
  segment: z.enum(['proximal', 'distal']),
  /** Site along the segment, fraction from its origin toward the hinge/end. */
  along: z.number().min(0).max(1),
  /** Anatomical displacement direction; resolved to a concrete frame at graft. */
  direction: z.object({ anterior: z.number(), proximal: z.number(), lateral: z.number() }),
  /** θ → displacement as a FRACTION of the segment length (piecewise-linear stations). */
  ramp: z.array(z.object({ thetaDeg: z.number(), value: z.number() })).min(2),
  /** Influence radius around the site, fraction of the segment length. */
  radiusFraction: z.number().positive().max(0.6),
  /**
   * The family's typical belly RADIUS as a fraction of the segment length —
   * the corpus median, a prior. The graft's amplitude scale is
   * `radiusPrior × segment` so the kernel's scale is in RADIUS units by
   * construction (ramp values are fractions of the belly radius, ADR-028
   * §6); the Document run replaces it with the mesh's MEASURED ring radius,
   * and falls back to this prior — correctly calibrated, and marked — only
   * when the mesh cannot be measured (Codex P2, #107 round four).
   */
  radiusPrior: z.number().positive().max(1),
  /** Share of the chain weight the leaf takes at the site center (tapers to 0 at the radius). */
  weightShare: z.number().positive().max(0.8),
  provenance: z.array(z.string().min(1)).min(1),
});
export type HingeLeaf = z.infer<typeof hingeLeafSchema>;

/**
 * ADR-028 E3: one crease/stretch corrective MORPH riding a hinge. The
 * declaration is a NAME and a θ-ramp and nothing else — the shape itself is
 * derived per character at bake (D2), never shipped in a family template,
 * because a family-shared number cannot carry a character's geometry (§6, the
 * same law that keeps leaf ramps in fractions).
 *
 * The name wears the `_corr` class like every other corrective, bones
 * included. That is not decoration: re-import ADOPTS correctives it already
 * finds rather than grafting them twice (#110), and the name class is how a
 * previously-augmented model is recognised. A morph target is a shape key in
 * every DCC that opens the file, so the class travels where the user can see
 * it — which is intended.
 */
export const hingeMorphSchema = z.object({
  /** Morph target name — wears the `_corr` class, and is the key the ramp drives. */
  name: z.string().regex(/_corr$/),
  /** θ → morph weight (piecewise-linear stations; the Daz two-stage 75°/135° shape). */
  ramp: z.array(z.object({ thetaDeg: z.number(), value: z.number() })).min(2),
  provenance: z.array(z.string().min(1)).min(1),
});
export type HingeMorph = z.infer<typeof hingeMorphSchema>;

export const hingeSpecSchema = z.object({
  /** The bone whose local rotation carries the bend (e.g. R_Forearm). */
  driver: z.string().min(1),
  /** The chain's end bone (e.g. R_Hand) — with the driver it defines the limb axis. */
  end: z.string().min(1),
  /** Helper bone name — MUST wear the `_corr` corrective class (§6e's one vocabulary). */
  helper: z.string().regex(/_corr$/),
  /** The helper's share of the bend — 0.5 for the classic half-rotation bone. */
  factor: z.number().min(0).max(1),
  /**
   * Direction flexion carries the end bone, in the CANONICAL FACING FRAME:
   * +Z is where the character faces, +Y is up. NEVER a world constant — the
   * import pipeline PROMOTES facing (the committed goblin arrives +Z and is
   * promoted to +X), so the graft rotates this vector by the rig's own
   * measured `facing` before crossing it with the limb (Codex P1, #103).
   * Elbows: (0,0,1). Knees: (0,0,−1).
   */
  anterior: vec3Schema,
  /** Crease-band half-width as a fraction of the driver→end length. */
  bandFraction: z.number().positive().max(0.5),
  /** E2 muscle leaves riding this hinge — optional; absent means bones-only (E1). */
  leaves: z.array(hingeLeafSchema).optional(),
  /** E3 crease morphs riding this hinge — optional; absent means no crease (E1/E2 only). */
  morphs: z.array(hingeMorphSchema).optional(),
  /** Authoring trail — declarations only, same law as jointFrames. */
  provenance: z.array(z.string().min(1)).min(1),
});
export const hingeTemplateSchema = z.object({
  family: z.string().regex(/^[a-z0-9-]+$/),
  hinges: z.array(hingeSpecSchema).min(1),
});
export type HingeSpec = z.infer<typeof hingeSpecSchema>;
export type HingeTemplate = z.infer<typeof hingeTemplateSchema>;

/**
 * ADR-029: a family's pelvis-ownership RULE — flesh below a spine STATION
 * belongs to the bone the thighs hang from. Declared in ONTOLOGY ids (the
 * rule is about the body, not a generator's names); the kernel resolves
 * owner, donors, station point and spine axis against each rig's mapping and
 * bind. Measured disease (2026-08-22, six Tripo bodies): the generator's root
 * stack sits at hip height and paints the buttock to the first spine bone,
 * so a bow swings the buttock forward about the hips.
 */
export const pelvisOwnershipSchema = z.object({
  family: z.string().regex(/^[a-z0-9-]+$/),
  /** Ontology id of the station joint — flesh below it (along the spine) is the pelvis's. */
  station: z.string().min(1),
  /** Ontology ids whose paint below the station is re-owned (the spine chain). */
  donors: z.array(z.string().min(1)).min(1),
  /** Seam ramp above the station as a fraction of the station's own segment length. */
  seamFraction: z.number().positive().max(1),
  provenance: z.array(z.string().min(1)).min(1),
});
export type PelvisOwnership = z.infer<typeof pelvisOwnershipSchema>;

/**
 * A SOURCE family whose clips are authored relative to its own rest (SL:
 * identity rest rotations, every key is a pose OF that rest) — for the named
 * ontology ids, retargeting from it plays REST-ANCHORED (the ADR-019
 * amendment's transport): source rest lands on the target's own rest and
 * motion carries as world-faithful deltas. Without it, absolute transport
 * imposes the source skeleton's rest GEOMETRY on the target — measured
 * 2026-09-22 on the Avery anchor: an identity SL pelvis lands 5.1° off the
 * Hip's rest and the neck 16.8° back (SL's vertical neck vs Tripo's forward
 * one), a constant on every OAR pose. Children cancel the anchor in their
 * prefix, so limbs keep their world pose exactly.
 */
export const restAnchorSchema = z.object({
  /** The SOURCE family whose clips are rest-relative. */
  family: z.string().regex(/^[a-z0-9-]+$/),
  /** TARGET families the anchoring is declared (and field-read) for. */
  pairedFamilies: z.array(z.string().regex(/^[a-z0-9-]+$/)).min(1),
  /** Ontology ids that play rest-anchored. */
  ontologyIds: z.array(z.string().min(1)).min(1),
  provenance: z.array(z.string().min(1)).min(1),
});
export type RestAnchor = z.infer<typeof restAnchorSchema>;

export const priorsBundleSchema = z.object({
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  /** Ontology version the lexicon's IDs were authored against (ADR-003 §6 compat rule). */
  ontologyVersion: z.string().regex(/^\d+\.\d+\.\d+$/),
  lexicon: z.array(lexiconEntrySchema).min(1),
  /**
   * Authored joint-frame overrides (the Hand Studio frame contract). Optional
   * and additive: a bundle without it is valid and reorientation simply uses
   * its measured/declared tiers — "no override" must never be a boot error the
   * way an empty lexicon is.
   */
  jointFrames: z.array(jointFrameSchema).optional(),
  /**
   * Family hinge-corrective templates (ADR-028). Optional and additive on the
   * same law as jointFrames: no template means the family simply has no
   * correctives — never a boot error. Disabling correctives is an explicit
   * absence HERE, in the declaration, not a silently missing bone downstream.
   */
  hingeTemplates: z.array(hingeTemplateSchema).optional(),
  /**
   * Family pelvis-ownership declarations (ADR-029). Optional and additive on
   * the same law: no declaration means the family's pelvis paint stands as
   * shipped — never a boot error.
   */
  pelvisOwnership: z.array(pelvisOwnershipSchema).optional(),
  /**
   * Source-family rest anchoring (the SL pelvis lean, 2026-09-22). Optional
   * and additive: absent means every pairing keeps plain absolute transport.
   */
  restAnchors: z.array(restAnchorSchema).optional(),
});
export type PriorsBundle = z.infer<typeof priorsBundleSchema>;

/** Unit-length / perpendicularity tolerance — matches the ontology's (ADR-003 §11). */
const UNIT_TOL = 1e-6;

function magnitude(v: Vec3): number {
  return Math.hypot(v.x, v.y, v.z);
}

function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

/** Validate raw data into a PriorsBundle, loudly (ADR-004 §7). */
export function parsePriorsBundle(raw: unknown): PriorsBundle {
  const parsed = priorsBundleSchema.safeParse(raw);
  if (!parsed.success) {
    throw new SchemaValidationError(
      'PriorsBundle',
      parsed.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`),
    );
  }
  const bundle = parsed.data;
  const problems: string[] = [];
  const seen = new Set<string>();
  for (const entry of bundle.lexicon) {
    if (seen.has(entry.ontologyId)) problems.push(`duplicate lexicon entry for "${entry.ontologyId}"`);
    seen.add(entry.ontologyId);
  }
  // Authored joint-frame overrides are DATA that becomes a reorientation
  // tier — a non-unit or non-perpendicular frame would feed a malformed twist
  // basis straight into the C·B law. Authored = full authority, so the
  // geometry gate is the ONLY thing standing between a typo and a sideways
  // fist: same gate the ontology applies to its own zeroPoseDirection/
  // zeroPoseSecondary (ADR-003 §11). And the table holds DECLARATIONS, not
  // measurements — every entry must say who authored it (the machine-checked
  // form of the 2026-07-14 reframe).
  const seenFrames = new Set<string>();
  for (const f of bundle.jointFrames ?? []) {
    const key = `${f.family}/${f.ontologyId}`;
    if (seenFrames.has(key)) problems.push(`duplicate joint-frame for ${key}`);
    seenFrames.add(key);
    if (Math.abs(magnitude(f.localPrimary) - 1) > UNIT_TOL) {
      problems.push(`joint-frame ${key} localPrimary is not unit length`);
    }
    if (Math.abs(magnitude(f.localSecondary) - 1) > UNIT_TOL) {
      problems.push(`joint-frame ${key} localSecondary is not unit length`);
    }
    const d = dot(f.localPrimary, f.localSecondary);
    if (Math.abs(d) > UNIT_TOL) {
      problems.push(`joint-frame ${key} localSecondary is not perpendicular to localPrimary (dot=${d.toFixed(6)})`);
    }
    if (!f.provenance.some((p) => p.startsWith('authored:'))) {
      problems.push(
        `joint-frame ${key} has no 'authored:' provenance — this table holds authored overrides, not measurements`,
      );
    }
  }
  // Hinge templates carry the same declaration discipline: one template per
  // family, one helper name per template, authored provenance on every hinge.
  const seenHingeFamilies = new Set<string>();
  for (const t of bundle.hingeTemplates ?? []) {
    if (seenHingeFamilies.has(t.family)) problems.push(`duplicate hinge template for family "${t.family}"`);
    seenHingeFamilies.add(t.family);
    const seenHelpers = new Set<string>();
    for (const h of t.hinges) {
      if (seenHelpers.has(h.helper)) problems.push(`hinge template "${t.family}" declares helper "${h.helper}" twice`);
      seenHelpers.add(h.helper);
      if (Math.abs(magnitude(h.anterior) - 1) > UNIT_TOL) {
        problems.push(`hinge template "${t.family}"/"${h.helper}" anterior is not unit length`);
      }
      if (!h.provenance.some((p) => p.startsWith('authored:'))) {
        problems.push(`hinge template "${t.family}"/"${h.helper}" has no 'authored:' provenance`);
      }
      for (const leaf of h.leaves ?? []) {
        if (seenHelpers.has(leaf.name)) problems.push(`hinge template "${t.family}" declares "${leaf.name}" twice`);
        seenHelpers.add(leaf.name);
        const d = leaf.direction;
        if (Math.hypot(d.anterior, d.proximal, d.lateral) < 1e-6) {
          problems.push(`leaf "${leaf.name}" has a zero anatomical direction — it names no displacement`);
        }
        if (!leaf.provenance.some((p) => p.startsWith('authored:'))) {
          problems.push(`leaf "${leaf.name}" has no 'authored:' provenance`);
        }
      }
      // Morph names share ONE namespace with the helper and leaf bones. They
      // are not bones, so nothing would collide at the rig level — but the
      // evaluator returns helpers and morphs from a single profile keyed by
      // name, and a driver that resolves `pose.name` against the wrong kind
      // would drive silently and wrongly. One namespace, one refusal.
      //
      // This runs AFTER the leaf loop on purpose (Codex P2, #113): the leaves
      // register their names in `seenHelpers`, so checking morphs first let a
      // morph share a name with a leaf declared below it and the collision
      // sailed through.
      const seenMorphs = new Set<string>();
      for (const m of h.morphs ?? []) {
        if (seenHelpers.has(m.name) || seenMorphs.has(m.name)) {
          problems.push(`hinge template "${t.family}" declares "${m.name}" twice`);
        }
        seenMorphs.add(m.name);
        // TEMPLATE-WIDE, not per hinge (Codex P2, #113): each hinge got a fresh
        // `seenMorphs`, and morph names never joined `seenHelpers` — so two
        // hinges could declare the SAME morph name, the graft would adopt one
        // shared mesh target for both, and their two drivers would overwrite
        // that influence every frame with whichever ran last winning.
        seenHelpers.add(m.name);
        if (!m.provenance.some((p) => p.startsWith('authored:'))) {
          problems.push(`morph "${m.name}" has no 'authored:' provenance`);
        }
        for (let k = 1; k < m.ramp.length; k += 1) {
          if (!(m.ramp[k]!.thetaDeg > m.ramp[k - 1]!.thetaDeg)) {
            problems.push(`morph "${m.name}" ramp stations are not strictly increasing in θ`);
          }
        }
        // THE PEAK IS 1, checked HERE and not only at derivation time.
        //
        // The derived target stores a FULL-STRENGTH correction and the runtime
        // multiplies it by the ramp, so any other peak silently under- or
        // over-applies it. `derivationAngle` enforces that — but only when a
        // derivation runs: on a re-import the target is ADOPTED and the check
        // is skipped entirely, so a bad declaration would drive wrongly
        // forever with nothing said. And on a fresh model the refusal arrived
        // only after the rig had been grafted and reweighted. A declaration is
        // wrong the moment it is read (Codex P2, #113).
        const peak = Math.max(...m.ramp.map((r) => r.value));
        if (Math.abs(peak - 1) > 1e-6) {
          problems.push(
            `morph "${m.name}" ramp peaks at ${peak} rather than 1 — the shape is derived at full strength and ` +
              `multiplied by the ramp at play time, so any other peak silently mis-applies it; shape the WIDTH`,
          );
        }
      }
    }
  }
  const seenPelvisFamilies = new Set<string>();
  for (const p of bundle.pelvisOwnership ?? []) {
    if (seenPelvisFamilies.has(p.family)) problems.push(`duplicate pelvis ownership for family "${p.family}"`);
    seenPelvisFamilies.add(p.family);
    if (!p.provenance.some((x) => x.startsWith('authored:'))) {
      problems.push(`pelvis ownership "${p.family}" has no 'authored:' provenance`);
    }
  }
  const seenAnchorFamilies = new Set<string>();
  for (const a of bundle.restAnchors ?? []) {
    if (seenAnchorFamilies.has(a.family)) problems.push(`duplicate rest anchors for family "${a.family}"`);
    seenAnchorFamilies.add(a.family);
    if (!a.provenance.some((x) => x.startsWith('authored:'))) {
      problems.push(`rest anchors "${a.family}" have no 'authored:' provenance`);
    }
  }
  if (problems.length > 0) throw new SchemaValidationError('PriorsBundle', problems);
  return bundle;
}

/** The family's hinge template, or null — the caller treats absence as "no correctives declared". */
export function hingeTemplateFor(bundle: PriorsBundle, family: string): HingeTemplate | null {
  return bundle.hingeTemplates?.find((t) => t.family === family) ?? null;
}

/**
 * Look up the authored joint-frame override for a (family, ontologyId), or
 * null when the bundle has none — the caller falls through to its
 * measured/declared tiers.
 */
export function jointFrameFor(
  bundle: PriorsBundle,
  family: string,
  ontologyId: string,
  otherFamily?: string,
  role?: 'source' | 'target',
): JointFrame | null {
  return (
    bundle.jointFrames?.find((f) => f.family === family && f.ontologyId === ontologyId &&
      (f.appliesTo === undefined || f.appliesTo === role) &&
      (f.pairedFamilies === undefined || (otherFamily !== undefined && f.pairedFamilies.includes(otherFamily)))) ?? null
  );
}

/**
 * A lexicon that names bones the loaded ontology does not have, or that was
 * authored against an incompatible ontology version, is OUR data bug —
 * reject at the seam, before any scoring runs (ADR-004 §7). Version rule =
 * ADR-003 §6: same major, loaded minor ≥ authored minor (minor bumps are
 * additive, so older-minor priors stay valid under a newer ontology — never
 * the reverse, never across majors). (Codex, PR #14.)
 */
export function requirePriorsCompatible(bundle: PriorsBundle, ontology: Ontology): void {
  const parse = (v: string): [number, number] => {
    const [maj = 0, min = 0] = v.split('.').map(Number);
    return [maj, min];
  };
  const [bundleMajor, bundleMinor] = parse(bundle.ontologyVersion);
  const [loadedMajor, loadedMinor] = parse(ontology.version);
  if (bundleMajor !== loadedMajor || loadedMinor < bundleMinor) {
    throw new SchemaValidationError('PriorsBundle', [
      `priors were authored against ontology ${bundle.ontologyVersion} but ${ontology.version} is loaded — ` +
        `compatible = same major, loaded minor ≥ authored minor (ADR-003 §6)`,
    ]);
  }
  const problems: string[] = [];
  const unknown = bundle.lexicon.map((e) => e.ontologyId).filter((id) => !ontology.byId.has(id));
  if (unknown.length > 0) {
    problems.push(
      `lexicon names ${unknown.length} bone(s) unknown to ontology ${ontology.version}: ${unknown.join(', ')}`,
    );
  }
  // Joint-frame priors key on ontology IDs too — a frame for a bone the loaded
  // ontology doesn't have is our data bug, caught at the same seam.
  const unknownFrames = (bundle.jointFrames ?? [])
    .filter((f) => !ontology.byId.has(f.ontologyId))
    .map((f) => `${f.family}/${f.ontologyId}`);
  if (unknownFrames.length > 0) {
    problems.push(
      `joint-frames name ${unknownFrames.length} bone(s) unknown to ontology ${ontology.version}: ${unknownFrames.join(', ')}`,
    );
  }
  // A joint-frame for a TIP is meaningless (Codex, PR #24): tips carry no
  // zeroPoseDirection/zeroPoseSecondary and never articulate, so a stored
  // primary+secondary pair has no contract to override and must never reach the
  // reorientation tier. Reject at the same seam.
  const tipFrames = (bundle.jointFrames ?? [])
    .filter((f) => ontology.byId.get(f.ontologyId)?.tip === true)
    .map((f) => `${f.family}/${f.ontologyId}`);
  if (tipFrames.length > 0) {
    problems.push(
      `joint-frames name ${tipFrames.length} TIP bone(s), which do not articulate: ${tipFrames.join(', ')}`,
    );
  }
  // Pelvis-ownership declarations key on ontology IDs too (ADR-029) — a
  // station or donor the loaded ontology lacks is our data bug (a typo, an
  // incompatible ontology), caught HERE rather than surfacing downstream as
  // a per-rig "unresolved" marker that reads like a specimen property
  // (Codex P2, #108).
  const unknownPelvis = (bundle.pelvisOwnership ?? []).flatMap((p) =>
    [p.station, ...p.donors].filter((id) => !ontology.byId.has(id)).map((id) => `${p.family}/${id}`),
  );
  if (unknownPelvis.length > 0) {
    problems.push(
      `pelvis-ownership names ${unknownPelvis.length} bone(s) unknown to ontology ${ontology.version}: ${unknownPelvis.join(', ')}`,
    );
  }
  // A rest anchor on an unknown bone is a typo; on a tip or twist-anchored
  // bone it contradicts the ontology's own transport declaration.
  for (const a of bundle.restAnchors ?? []) {
    for (const id of a.ontologyIds) {
      const bone = ontology.byId.get(id);
      if (bone === undefined) {
        problems.push(`rest anchors "${a.family}" name bone "${id}" unknown to ontology ${ontology.version}`);
      } else if (bone.tip || bone.twistAnchoredTransport) {
        problems.push(`rest anchors "${a.family}" name "${id}", which is a tip or twist-anchored bone`);
      }
    }
  }
  if (problems.length > 0) throw new SchemaValidationError('PriorsBundle', problems);
}

/** The family's pelvis-ownership declaration, or null — absence means "the paint stands as shipped". */
export function pelvisOwnershipFor(bundle: PriorsBundle, family: string): PelvisOwnership | null {
  return bundle.pelvisOwnership?.find((p) => p.family === family) ?? null;
}

/**
 * Whether retargeting FROM `sourceFamily` TO `targetFamily` plays `ontologyId`
 * rest-anchored by declaration. False whenever either family is absent or the
 * pairing is undeclared — the plain absolute law stands.
 */
export function sourceRestAnchored(
  bundle: PriorsBundle,
  sourceFamily: string | undefined,
  targetFamily: string | undefined,
  ontologyId: string,
): boolean {
  if (sourceFamily === undefined || targetFamily === undefined) return false;
  return (bundle.restAnchors ?? []).some(
    (a) => a.family === sourceFamily && a.pairedFamilies.includes(targetFamily) && a.ontologyIds.includes(ontologyId),
  );
}
